import * as React from 'react';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';
import FormErrorMessage from '../../../components/ErrorMessage';
import DateTimeInput from '../../../components/DateTimeInput';

// TYPES
import { closeModalType } from '../../../components/GlobalModal';
import { IncidentFormTypes } from '../IncidentSubmit';

interface AddEmergencyServiceModalTypes {
	closeModal?: closeModalType;
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
}

const AddEmergencyServiceModal: React.FC<AddEmergencyServiceModalTypes> = ({
	closeModal,
	incidentFormData,
	setIncidentFormData
}) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const initialValues = {
		name: '',
		dateAndTime: '',
		attend: false,
		dateAndTimeAttended: ''
	};

	const validationSchema = Yup.object({
		name: Yup.string().required('Required'),
		dateAndTime: Yup.string().required('Required'),
		dateAndTimeAttended: Yup.string().when('attend', {
			is: true,
			then: Yup.string().required('Required')
		})
	});

	const submitHandler = (values: any) => {
		const formData = {
			name: values.name as string,
			dateAndTime: values.dateAndTime as string,
			attend: values.attend as boolean,
			dateAndTimeAttended: values.dateAndTimeAttended as string
		};

		const currentEmergencyServices = incidentFormData.emergencyServices || [];

		const newEmergencyServices = [...currentEmergencyServices, formData];

		setIncidentFormData((prevState) => ({
			...prevState,

			emergencyServices: newEmergencyServices
		}));

		if (closeModal) {
			closeModal();
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="form-group">
													<label htmlFor="name" className="label">
														Emergency service contacted
													</label>

													<div className="form-field">
														<Field name="name" id="name" type="text" className="form-input" />

														<ErrorMessage name="name" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label className="label">Date and time of contact</label>

													<DateTimeInput
														dateTimeString={formik.values.dateAndTime}
														onChange={(val: string) => formik.setFieldValue('dateAndTime', val)}
														excludeFuture
													/>
												</div>

												<div className="form-group">
													<label className="label">Did the emergency services attend after contact?</label>

													<div className="form-field radio-group">
														<div className="radio-row">
															<Field
																name="attend"
																id="attend"
																type="radio"
																value={true}
																checked={formik.values.attend === true}
																onChange={() => formik.setFieldValue('attend', true)}
															/>

															<span className="radio-option">Yes</span>
														</div>

														<div className="radio-row">
															<Field
																name="attend"
																id="attend"
																type="radio"
																value={false}
																checked={formik.values.attend === false}
																onChange={() => formik.setFieldValue('attend', false)}
															/>

															<span className="radio-option">No</span>
														</div>

														<ErrorMessage name="attend" component="div" className="error-message" />
													</div>
												</div>

												{formik.values.attend === true && (
													<div className="form-group">
														<label className="label">Date and time the emergency services attended</label>

														<DateTimeInput
															dateTimeString={formik.values.dateAndTimeAttended}
															onChange={(val: string) => formik.setFieldValue('dateAndTimeAttended', val)}
															excludeFuture
														/>
													</div>
												)}

												<Button className="btn-primary full-width" type="submit">
													Add to the list
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default AddEmergencyServiceModal;
